import axios from "axios";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { getAuthRegistry } from "@/lib/auth/AuthRegistry";
import { notifySessionExpired } from "@/lib/session/sessionExpired";
import { bucketFromUrl, spanName, datesVariantSuffix } from "@/lib/telemetry/bucket";
import i18n from "@/lib/i18n/i18n";

/** The demo read-only notice, in the user's language; returns its text. sonner dedupes by id, so a burst of
 *  refused mutations shows it once. Also for apps whose own clients meet the same refusal (apps/inbox:
 *  inbox-web answers 403 {"code": "demo"}). */
export function showDemoNotice(): string {
  const message = i18n.t("demo.read_only",
    "This is a demo account — changes are disabled. Sign up to get your own salon.");
  toast.warning(i18n.t("demo.title", "Demo mode"), { id: "demo-guard", description: message });
  return message;
}

export const apiClient = axios.create({
  baseURL: "",  // Configured by SsoPage or main.tsx after login based on user's region
  headers: { "Content-Type": "application/json" },
});

// Inject Bearer token from AuthRegistry into all requests
apiClient.interceptors.request.use(req => {
  const { accessToken } = getAuthRegistry().getTokens();
  if (accessToken) {
    req.headers = req.headers || {};
    req.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Log all outgoing requests
  /*
  const method = req.method?.toUpperCase() || "GET";
  const url = req.baseURL ? req.baseURL + req.url : req.url;
  // Log params and data if present
  const params = req.params ? JSON.stringify(req.params) : "";
  const data = req.data ? JSON.stringify(req.data) : "";
  console.log(
    `[API REQUEST] ${method} ${url}` +
    (params ? ` | params: ${params}` : "") +
    (data ? ` | data: ${data}` : "")
  );
  */
  // --- TIMING + SENTRY SPAN INSTRUMENTATION ---
  (req as any).__t0 = performance.now();

  // start a manual span so we always get a child under the active transaction
  const url = new URL(req.url!, req.baseURL || window.location.origin);
  const bucket = bucketFromUrl(url.href);
  const { suffix: datesSuffix, count: datesCount } = datesVariantSuffix(url.href, bucket);
  (req as any).__telemetryBucket = bucket;
  (req as any).__sentrySpan = Sentry.startInactiveSpan?.({
    name: spanName(req.method, bucket) + datesSuffix, // ← add "(dates:N)" when relevant
    op: "http.client",
    attributes: {
      "http.method": (req.method || "GET").toUpperCase(),
      "http.url": url.href,
      "http.target": url.pathname + url.search,
      "http.route": bucket,
      "telemetry.bucket": bucket,
      ...(datesCount !== undefined ? { "telemetry.dates.count": datesCount } : {}),
    },
  });

  return req;
});

apiClient.interceptors.response.use(
  (response) => {
    const t0 = (response.config as any).__t0 as number | undefined;
    const span = (response.config as any).__sentrySpan;
    const url = response.request?.responseURL || response.config.url;

    // Normalize to relative path (pathname + search)
    let lookupUrl = url;
    try {
      const urlObj = new URL(url, response.config.baseURL || window.location.origin);
      lookupUrl = urlObj.pathname + urlObj.search; // e.g., "/api/v1/company/timetable/schedule"
    } catch {
      // If URL parsing fails, use as-is
      lookupUrl = url;
    }

    // Try to enrich with Resource Timing (DNS/connect/TTFB/etc.)
    try {
      if (url) {
        const entries = performance.getEntriesByName(lookupUrl, "resource") as PerformanceResourceTiming[];
        const rt = entries?.[entries.length - 1]; // latest matching entry
        if (rt) {
          // Useful fields (ms)
          const dns = rt.domainLookupEnd - rt.domainLookupStart;
          const connect = rt.connectEnd - rt.connectStart;
          const tls = rt.secureConnectionStart > 0 ? (rt.connectEnd - rt.secureConnectionStart) : 0;
          const ttfb = rt.responseStart - rt.requestStart;
          const download = rt.responseEnd - rt.responseStart;
          const total = rt.responseEnd - rt.startTime;

          // Console for quick local debugging
          // eslint-disable-next-line no-console
          console.log("[XHR TIMING]", {
            url,
            status: response.status,
            dns, connect, tls, ttfb, download, total,
          });

          // Attach to Sentry span if present
          if (span?.setAttribute) {
            span.setAttribute("net.dns", dns);
            span.setAttribute("net.connect", connect);
            span.setAttribute("net.tls", tls);
            span.setAttribute("http.ttfb", ttfb);
            span.setAttribute("http.download", download);
            span.setAttribute("http.duration", total);
          }
        }
      }
    } catch { /* ignore timing read errors */ }

    if (span) {
      span.setAttribute?.("http.status_code", response.status);
      try {
        const bucket = bucketFromUrl(lookupUrl);
        const { suffix, count } = datesVariantSuffix(lookupUrl, bucket);
        // update name to include dates suffix (consistent with request)
        // @ts-ignore — not in public types, but Sentry accepts it on the envelope
        if (suffix) span.updateName?.(spanName(response.config.method, bucket) + suffix);
        span.setAttribute?.("http.route", bucket);
        span.setAttribute?.("telemetry.bucket", bucket);
        if (count !== undefined) span.setAttribute?.("telemetry.dates.count", count);
      } catch { }
      span.end?.();
    }

    // fallback plain duration if RT was unavailable
    if (t0) {
      const dur = performance.now() - t0;
      // eslint-disable-next-line no-console
      console.log("[XHR DURATION]", (response.config.url || ""), `${Math.round(dur)}ms`);
    }

    return response;
  },
  (error) => {
    // Show a global "session expired" once on auth failures
    const status = error?.response?.status;
    const url = String(error?.config?.url || "");
    const isLoginCall = /\/auth\/jwt\/login\b/.test(url);
    // Client-portal endpoints (require_client on the backend) answer 401/403
    // for CLIENT-account reasons — not logged in as a client, deleted account
    // (403 account_deleted) — never because the widget's public session died.
    // They must surface as normal errors, not the fullscreen overlay.
    const isClientPortalCall = /\/public\/(client\/|waitlist\/mine\b)/.test(url);

    // Demo read-only guard: the backend blocks mutations for demo companies
    // with 403 {"demo": true, ...}. This must be detected BEFORE the generic
    // 401/403 session-expired branch below, or demo users would be kicked to
    // the login screen on every edit attempt.
    if (status === 403 && error?.response?.data?.demo === true) {
      const message = showDemoNotice();
      const span = error?.config && (error.config as any).__sentrySpan;
      if (span) {
        span.setAttribute?.("demo.blocked", true);
        span.end?.();
      }
      // Rewrite the message so any feature-level error panel shows the
      // friendly text instead of "Request failed with status code 403".
      error.message = message;
      if (error.response?.data) {
        error.response.data.error = message;
        error.response.data.message = message;
      }
      (error as any).demoBlocked = true;
      throw error;
    }

    // Subscription read-only guard: the backend blocks mutations for expired
    // licenses with 402 {"code": "license_restricted"}. Show a renewal toast
    // instead of a generic failure (docs/codebase/subscription/README.md §9).
    if (status === 402 && error?.response?.data?.code === "license_restricted") {
      const billingUrl = error?.response?.data?.billing_url || "https://id.boona.app/billing";
      const message = i18n.t("license.restricted",
        "Your subscription has expired — the account is read-only. Renew to make changes again.");
      toast.error(i18n.t("license.restricted_title", "Subscription expired"), {
        id: "license-guard",
        description: message,
        action: {
          label: i18n.t("license.renew", "Renew"),
          onClick: () => window.open(billingUrl, "_blank", "noopener"),
        },
      });
      const span = error?.config && (error.config as any).__sentrySpan;
      if (span) {
        span.setAttribute?.("license.blocked", true);
        span.end?.();
      }
      error.message = message;
      if (error.response?.data) {
        error.response.data.error = message;
        error.response.data.message = message;
      }
      (error as any).licenseBlocked = true;
      throw error;
    }

    // Seat limit: adding/restoring a master beyond the licensed seats returns
    // 409 {"code": "seat_limit_reached", "seats_licensed": N}.
    if (status === 409 && error?.response?.data?.code === "seat_limit_reached") {
      const billingUrl = error?.response?.data?.billing_url || "https://id.boona.app/billing";
      const seats = error?.response?.data?.seats_licensed;
      const message = i18n.t("license.seat_limit",
        "All {{seats}} licensed seats are in use. Upgrade your plan to add more masters.",
        { seats: seats ?? "" });
      toast.warning(i18n.t("license.seat_limit_title", "Seat limit reached"), {
        id: "seat-limit",
        description: message,
        action: {
          label: i18n.t("license.upgrade", "Upgrade"),
          onClick: () => window.open(`${billingUrl}?seats=${(seats ?? 0) + 1}`, "_blank", "noopener"),
        },
      });
      const span = error?.config && (error.config as any).__sentrySpan;
      if (span) {
        span.setAttribute?.("license.seat_limit", true);
        span.end?.();
      }
      error.message = message;
      if (error.response?.data) {
        error.response.data.error = message;
        error.response.data.message = message;
      }
      (error as any).seatLimitBlocked = true;
      throw error;
    }

    // For 401/403 (except login and client-portal calls), notify session
    // expired and suppress error propagation. This prevents unhandled
    // rejection noise in Sentry while the user is redirected to login.
    if ((status === 401 || status === 403) && !isLoginCall && !isClientPortalCall) {
      notifySessionExpired();
      // End Sentry span before suppressing
      const span = error?.config && (error.config as any).__sentrySpan;
      if (span) {
        span.setStatus?.("error");
        span.setAttribute?.("error", "session_expired");
        span.end?.();
      }
      // Return a never-resolving promise to suppress further error propagation
      // The app is in a terminal state anyway — user will be redirected to login
      return new Promise(() => { });
    }

    const span = error?.config && (error.config as any).__sentrySpan;
    if (span) {
      span.setStatus?.("error");
      span.setAttribute?.("error", String(error?.message || "request failed"));
      try {
        const url = String(error?.config?.url || "");
        const bucket = bucketFromUrl(url);
        const { suffix, count } = datesVariantSuffix(url, bucket);
        // @ts-ignore — see note above
        if (suffix) span.updateName?.(spanName(error?.config?.method, bucket) + suffix);
        span.setAttribute?.("http.route", bucket);
        span.setAttribute?.("telemetry.bucket", bucket);
        if (count !== undefined) span.setAttribute?.("telemetry.dates.count", count);
      } catch { }
      span.end?.();
    }
    throw error;
  }
);
